import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Lock, Database, CheckCircle, Zap, Gift } from 'lucide-react';
import { useSalesTracking } from '../hooks/useSalesTracking';
import { usePageTitle } from '../hooks/usePageTitle';
import { trackProductPageView } from '../utils/analytics';
import { APP_NAME } from '../config/branding';

import IsometricMedicalRecords from '../components/IsometricMedicalRecords';

const ProtoPIMS: React.FC = () => {
  usePageTitle('Medical Records Beta - Free Veterinary Medical Records System');

  // Track sales link clicks
  useSalesTracking();

  useEffect(() => {
    trackProductPageView('proto_pims');
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="mb-4">
              <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-bold rounded-full">
                Medical Records Beta
              </span>
            </div>

            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              World's first fully integrated veterinary medical records
            </h1>

            <p className="text-xl text-gray-600 mb-8">
              Complete practice information management system (PIMS) with SOAP notes, patient records, and encounter documentation. Built by veterinarians, for veterinarians. Integrates seamlessly with our <Link to="/products/calendar" className="text-blue-600 hover:text-blue-700 underline">Calendar</Link>, <Link to="/products/ai-front-desk" className="text-blue-600 hover:text-blue-700 underline">AI Front Desk</Link>, and <Link to="/products/visit-transcriptions" className="text-blue-600 hover:text-blue-700 underline">Visit Transcriptions</Link>.
            </p>

            <div className="flex gap-4 mb-8">
              <Link
                to="/signup"
                className="bg-emerald-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-emerald-700 transition-colors"
              >
                Get started today
              </Link>
              <Link
                to="/contact"
                className="bg-gray-100 text-gray-900 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Request demo →
              </Link>
            </div>
          </div>

          <div className="relative h-[400px] w-full flex items-center justify-center bg-white rounded-3xl overflow-hidden">
            <IsometricMedicalRecords />
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Gift className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Integrated with Voice AI</h3>
              <p className="text-sm text-gray-600">Medical records should be simple. Voice AI is thoughtfully integrated into the system to help you document patient encounters.</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Complete SOAP Notes</h3>
              <p className="text-sm text-gray-600">Subjective, Objective, Assessment, Plan documentation with templates and body maps.</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Secure & Private</h3>
              <p className="text-sm text-gray-600">Enterprise-grade security and encrypted data storage for your practice.</p>
            </div>
          </div>
        </div>

        {/* Core Features Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Complete medical records system</h2>
          <p className="text-lg text-gray-600 mb-8">
            Everything you need to document patient encounters and maintain compliance.
          </p>

          <div className="bg-gray-50 rounded-lg p-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Patient demographics and owner records</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Multi-pet households with linked owners</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Encounter documentation with SOAP notes</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Vitals tracking (weight, temp, heart rate, respiration)</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Interactive body maps for marking findings</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Treatment plans and discharge instructions</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Digital consent forms and signatures</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Attachment storage for lab results and images</span>
                </div>
              </div>

              <div className="flex justify-center">
                <img
                  src="/soap.jpg"
                  alt="SOAP notes interface showing Subjective, Objective, Assessment, and Plan sections"
                  className="rounded-lg shadow-lg"
                  style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Encounter Workflow Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">13-section encounter documentation</h2>
          <p className="text-lg text-gray-600 mb-8">
            Our encounter system guides you through comprehensive documentation without getting in your way.
          </p>

          <div className="bg-gray-50 rounded-lg p-8">
            <div className="grid md:grid-cols-2 gap-8 items-start">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Intake & Chief Complaint</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Vitals & Measurements</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Subjective (History)</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Objective (Physical Exam)</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Assessment (Diagnosis)</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Plan (Treatment)</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Orders & Prescriptions</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Body Map Annotations</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Discharge Instructions</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Follow-Up Plans</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Attachments & Files</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Consent Forms</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Sign & Complete Encounter</span>
                </div>
              </div>

              <div className="flex justify-center items-center h-full">
                <img
                  src="/ai.jpg"
                  alt={`${APP_NAME} AI Summary`}
                  className="rounded-lg shadow-lg w-full"
                  style={{ objectFit: 'contain' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Integration Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Seamlessly integrated</h2>
          <p className="text-lg text-gray-600 mb-8">
            Medical Records works with the entire {APP_NAME} platform for complete practice automation.
          </p>

          <div className="bg-gray-50 rounded-lg p-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-blue-600" />
                <div>
                  <span className="font-semibold text-gray-900">Calendar & Patient Flow: </span>
                  <span className="text-gray-700">Click any pet to start an encounter directly from your schedule</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-blue-600" />
                <div>
                  <span className="font-semibold text-gray-900">Visit Transcriptions: </span>
                  <span className="text-gray-700">AI-generated SOAP notes flow directly into encounters</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-blue-600" />
                <div>
                  <span className="font-semibold text-gray-900">AI Front Desk: </span>
                  <span className="text-gray-700">Patient demographics automatically populate from phone calls</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-blue-600" />
                <div>
                  <span className="font-semibold text-gray-900">Multi-pet support: </span>
                  <span className="text-gray-700">One encounter per pet, all linked to the same appointment</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Beta Notice Section */}
        <div className="mb-16 bg-blue-50 border-2 border-blue-200 rounded-lg p-8">
          <div className="flex items-start gap-4">
            <Database className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Medical Records is in beta</h3>
              <p className="text-gray-700 mb-3">
                We're actively developing new features based on feedback from practices like yours. While the core medical records functionality is solid, we're still adding features.
              </p>
              <p className="text-gray-700">
                Your data is safe, secure, and always yours. But if you need features we don't have yet, we'll be honest about it. Join our beta and help shape the future of veterinary software.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Related Products Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Complete Your Practice Automation</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              to="/products/calendar"
              className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all group"
            >
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                Calendar & Patient Flow
              </h3>
              <p className="text-gray-600 mb-4">
                Visual scheduling with swim lanes and real-time patient tracking. Start encounters directly from your schedule.
              </p>
              <span className="text-blue-600 font-medium">Learn more →</span>
            </Link>

            <Link
              to="/products/visit-transcriptions"
              className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all group"
            >
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                Visit Transcriptions
              </h3>
              <p className="text-gray-600 mb-4">
                Record exams on your iPhone and get AI-generated SOAP notes that flow directly into Medical Records.
              </p>
              <span className="text-blue-600 font-medium">Learn more →</span>
            </Link>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to try fully integrated medical records?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Sign up today. No credit card required.
          </p>
          <div className="flex gap-4 justify-center">
            <Link
              to="/signup"
              className="bg-emerald-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-emerald-700 transition-colors"
            >
              Get started today
            </Link>
            <Link
              to="/contact"
              className="bg-gray-100 text-gray-900 px-8 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Talk to sales
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProtoPIMS;

